/**
 * The parts of recording a voice message that are worth testing without a
 * microphone: which container to ask for, how long a recording may run, and how
 * a length is written out.
 *
 * Kept out of the component for the same reason `cameraSession.ts` is — the
 * interesting behaviour is a set of small decisions, and a decision buried in a
 * `useEffect` can only be tested by rendering and faking browser media APIs.
 */

/** Longest recording the button will allow, in seconds. */
export const MAX_RECORDING_SECONDS = 120

/**
 * Containers to try, best first — and "best" means most widely *playable*, not
 * smallest.
 *
 * MP4/AAC leads, and that ordering is the whole point. Opus in WebM is
 * considerably more efficient and was tried first originally, which produced a
 * message a desktop could record and **an iPhone could not play at all**: iOS
 * Safari has no WebM support of any kind, in any container, and fails silently
 * rather than reporting an unsupported format.
 *
 * That is the worst possible shape for this feature. The people most likely to
 * send a voice message are on phones, and half the phones in a plant are
 * iPhones — so a recording made at the machine would reach a supervisor who
 * simply could not hear it, with nothing on screen saying why.
 *
 * MP4 is what Safari records natively and what Chrome, Edge and Firefox can all
 * record and play. A two-minute AAC message is a few hundred kB against Opus's
 * roughly two hundred, which is a trade worth making once for a file that plays
 * everywhere.
 *
 * The WebM entries stay as a fallback for a browser that can record nothing
 * else. Anything recorded that way is still unplayable on iOS, which is why the
 * player detects it and offers a download rather than a dead play button.
 */
export const PREFERRED_MIME_TYPES = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/ogg',
]

/**
 * The first container this browser can actually record.
 *
 * Returns undefined when none match, which is not a failure: handing
 * `MediaRecorder` no mimeType at all makes it use its own default, and that is a
 * better outcome than refusing to record on a browser we did not anticipate.
 */
export function pickRecordingMimeType(
  isSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type),
): string | undefined {
  return PREFERRED_MIME_TYPES.find(isSupported)
}

/**
 * A file extension matching what was recorded.
 *
 * Only for the display name — the server derives nothing from it and detects the
 * type from the bytes. A worker seeing "poruka.webm" in a list still recognises
 * it as theirs, which "blob" would not manage.
 */
export function extensionFor(mimeType: string): string {
  const container = mimeType.split(';')[0]

  return (
    {
      'audio/webm': 'webm',
      'audio/ogg': 'ogg',
      'audio/mp4': 'm4a',
      'audio/mpeg': 'mp3',
      'audio/wav': 'wav',
    }[container] ?? 'webm'
  )
}

/** `0:07`, `1:42`, `12:05` — never `1:5`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

  const whole = Math.floor(seconds)
  const minutes = Math.floor(whole / 60)

  return `${minutes}:${String(whole % 60).padStart(2, '0')}`
}

/**
 * Turn a finished recording into something the form can send.
 *
 * The duration is passed in rather than read off the blob, and that is not
 * laziness. A `MediaRecorder` writes **no duration into the WebM header**, so
 * loading its own output into an `<audio>` element reports `Infinity` until the
 * file is seeked to the end. The recorder counted the seconds as they passed;
 * that number is the only reliable one anybody has.
 */
export function recordingToFile(blob: Blob, mimeType: string, index = 0): File {
  const type = blob.type || mimeType

  return new File([blob], `glasovna-poruka-${index + 1}.${extensionFor(type)}`, { type })
}

/**
 * Why a `getUserMedia` call failed, in Bosnian.
 *
 * "NotAllowedError" on screen tells a worker nothing. Each of these is a
 * different thing to do about it, which is the only reason to distinguish them.
 */
export function describeMicrophoneError(error: unknown): string {
  const name = error instanceof Error ? error.name : ''

  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Pristup mikrofonu nije dozvoljen. Dozvolite ga u podešavanjima pregledača i pokušajte ponovo.'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Mikrofon nije pronađen na ovom uređaju.'
    case 'NotReadableError':
      return 'Mikrofon koristi druga aplikacija. Zatvorite je i pokušajte ponovo.'
    default:
      return 'Snimanje nije moguće na ovom uređaju.'
  }
}

/**
 * Whether recording is possible here at all.
 *
 * `navigator.mediaDevices` is **undefined outside a secure context**, so this is
 * false over plain http — which is exactly how the app will be reached from the
 * floor if nobody puts TLS in front of it. The screen has to say so plainly
 * rather than offering a button that cannot work.
 */
/**
 * Whether this browser can play a recording of the given type.
 *
 * Exists because of one specific failure: an `<audio>` element handed a WebM
 * file on iOS does not error usefully — it reports readiness and then plays
 * nothing, so the user presses play, watches a bar that does not move, and
 * concludes the app is broken. Asking first turns that into a sentence and a
 * download link.
 *
 * `canPlayType` answers "", "maybe" or "probably". Only the empty string is a
 * definite no, so anything else is treated as playable and the element gets to
 * try — a browser that says "maybe" and succeeds is the common case.
 */
export function canPlayType(
  mimeType: string | null | undefined,
  probe: (type: string) => string = (type) => {
    if (typeof document === 'undefined') return 'maybe'

    return document.createElement('audio').canPlayType(type)
  },
): boolean {
  if (!mimeType) return true

  /*
   * The server stores the type detected from the bytes, and an audio-only WebM
   * is detected as `video/webm` — the container genuinely is one. Ask about it
   * as audio: the question is whether this browser can decode the stream, not
   * what the container is called.
   */
  const asAudio = mimeType.replace(/^video\//, 'audio/')

  return probe(asAudio) !== '' || probe(mimeType) !== ''
}

export function canRecord(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  )
}
